import axios from "axios";

export function getJSONFromLLMResponse(rawText) {
  if (!rawText || typeof rawText !== "string") return null;

  const cleanedText = rawText.trim();
  const fencedJsonMatch = cleanedText.match(/```json\s*([\s\S]*?)```/i);
  const fencedMatch = cleanedText.match(/```\s*([\s\S]*?)```/);
  const candidate = (fencedJsonMatch?.[1] || fencedMatch?.[1] || cleanedText)
    .replace(/\n/g, "")
    .trim();

  try {
    return JSON.parse(candidate);
  } catch (error) {
    const objectMatch = candidate.match(/\{[\s\S]*}/);
    if (objectMatch?.[0]) {
      try {
        return JSON.parse(objectMatch[0]);
      } catch (innerError) {
        console.error(innerError);
      }
    }

    const arrayMatch = candidate.match(/\[[\s\S]*]/);
    if (arrayMatch?.[0]) {
      try {
        return JSON.parse(arrayMatch[0]);
      } catch (innerError) {
        console.error(innerError);
      }
    }

    console.error(error);
    return null;
  }
}

export function normalizeSuggestionMatches(matches) {
  if (!Array.isArray(matches)) return [];
  return matches
    .map(match => {
      if (typeof match === "string") return match;
      return (
        match?.title || match?.category || match?.name || match?.label || ""
      );
    })
    .map(label => `${label || ""}`.trim())
    .filter(Boolean);
}

export async function executeSuggestionPrompt({
  promptsApiUrl,
  body,
  headers,
  onLog,
  logMeta = {}
}) {
  if (!promptsApiUrl) {
    throw new Error("Prompts API URL ist nicht konfiguriert.");
  }

  const promptExecUrl = `${promptsApiUrl}api/eo-prompt/exec`;
  const requestConfig = headers ? { headers } : undefined;

  if (typeof onLog === "function") {
    onLog("SUGGEST_REQUEST_START", {
      ...logMeta,
      promptShortId: body?.promptShortId,
      principalId: body?.principal_id,
      user: body?.user,
      payloadLength: (body?.payload || "").length,
      url: promptExecUrl
    });
  }

  const response = await axios.post(promptExecUrl, body, requestConfig);

  if (typeof onLog === "function") {
    onLog("SUGGEST_REQUEST_SUCCESS", {
      ...logMeta,
      hasText: Boolean(response?.data?.text),
      hasResponse: Boolean(response?.data?.response)
    });
  }

  const parsed =
    getJSONFromLLMResponse(response?.data?.text) ||
    getJSONFromLLMResponse(response?.data?.response) ||
    response?.data;
  const matches = normalizeSuggestionMatches(parsed?.matches || []);

  if (typeof onLog === "function") {
    onLog("SUGGEST_PARSE_RESULT", {
      ...logMeta,
      matchesCount: matches.length
    });
  }

  return matches;
}

export function getCurrentUserIdFromSession(currentUser, sessionString = "") {
  const fromStore = currentUser?.id || currentUser?._id;
  if (fromStore) return fromStore;

  try {
    const session = JSON.parse(sessionString || "{}");
    return session?.user_id || "";
  } catch (error) {
    return "";
  }
}

function buildExistingItemsPayload({
  currentSuggestionType,
  currentLabels,
  mainCategoryLabel
}) {
  const safeCurrentLabels = Array.isArray(currentLabels)
    ? currentLabels.filter(Boolean)
    : [];

  if (currentSuggestionType === "feature") {
    return [
      "Bereits gesetzte Merkmale:",
      safeCurrentLabels.length ? safeCurrentLabels.join(", ") : "-"
    ].join("\n");
  }

  return [
    `Haupt-Kategorie (nie vorschlagen): ${mainCategoryLabel || "-"}`,
    "Bereits gesetzte Kategorien (zusaetzlich):",
    safeCurrentLabels.length ? safeCurrentLabels.join(", ") : "-"
  ].join("\n");
}

function buildSuggestionRulesPayload({ currentSuggestionType, contextPhrase }) {
  const itemLabel =
    currentSuggestionType === "feature" ? "Merkmale" : "Kategorien";

  return [
    "Wichtige Regeln:",
    `- Schlagen Sie nur ${itemLabel} aus der Liste 'Moegliche ${itemLabel}' vor.`,
    `- Schlagen Sie keine bereits gesetzten ${itemLabel} vor.`,
    currentSuggestionType === "category"
      ? "- Schlagen Sie niemals die Haupt-Kategorie vor."
      : "",
    `- Nennen Sie nur inhaltlich passende Eintraege ${contextPhrase ||
      ""}.`.trim(),
    '- Antwortformat: reines JSON-Objekt mit Feld "matches" als String-Array.',
    '  Beispiel: {"matches":["Eintrag 1","Eintrag 2"]}'
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildSuggestionPromptPayload({
  currentSuggestionType,
  title,
  text,
  currentLabels,
  mainCategoryLabel,
  possibleItemsPayload,
  entityLabelGenitive,
  contextPhrase
}) {
  return [
    `Titel ${entityLabelGenitive || "des Eintrags"}: ${title || ""}`,
    "",
    `Text ${entityLabelGenitive || "des Eintrags"}:`,
    text || "-",
    "",
    buildExistingItemsPayload({
      currentSuggestionType,
      currentLabels,
      mainCategoryLabel
    }),
    "",
    possibleItemsPayload || "",
    "",
    buildSuggestionRulesPayload({ currentSuggestionType, contextPhrase }),
    ""
  ].join("\n");
}
